import React, { useEffect, useRef, useState } from 'react';
import { useContracts } from '../../../context/ContractContext';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Loader from '../../common/Loader';
import InlineLoader from '../../common/InlineLoader';
import { formatUSDT, formatDateShort, shortAddr } from '../../../utils/format';
import { useTxRunner } from './AdminHelpers';

const PAGE = 20;

export default function WithdrawOps() {
  const { views, viewsW } = useContracts();
  const { busy, run } = useTxRunner();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pg, setPg] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [scanLimit, setScanLimit] = useState('500');
  const mountedRef = useRef(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      try {
        const res = await views.getPendingWithdraws(pg, PAGE);
        if (!mountedRef.current) return;
        const rows = [];
        for (let i = 0; i < res.ids.length; i++) {
          rows.push({
            id: res.ids[i].toString(),
            userId: res.userIds[i].toString(),
            wallet: res.wallets[i],
            amount: res.amounts[i],
            requestedAt: res.requestedAts[i]
          });
        }
        setItems(rows);
        setTotal(Number(res.total.toString()));
        setSelected(new Set());
        setLoading(false);
      } catch (_) {
        if (mountedRef.current) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { mountedRef.current = false; };
  }, [views, pg, refresh]);

  const refetch = () => setRefresh((v) => v + 1);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((x) => x.id)));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE));

  const approveOne = async (id) => {
    const ok = await run(`Approving #${id}`, () => viewsW.approveWithdraw(id));
    if (ok) refetch();
  };
  const rejectOne = async (id) => {
    if (!window.confirm(`Reject request #${id}? Amount will be returned to user's balance.`)) return;
    const ok = await run(`Rejecting #${id}`, () => viewsW.rejectWithdraw(id));
    if (ok) refetch();
  };
  const approveBatch = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = await run(`Approving ${ids.length} requests`, () => viewsW.approveWithdrawBatch(ids));
    if (ok) refetch();
  };
  const rejectBatch = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Reject ${ids.length} requests?`)) return;
    const ok = await run(`Rejecting ${ids.length} requests`, () => viewsW.rejectWithdrawBatch(ids));
    if (ok) refetch();
  };
  const approveAll = async () => {
    const lim = Number(scanLimit) || 500;
    if (!window.confirm(`Approve all pending in first ${lim} requests scanned?`)) return;
    const ok = await run(`Approving all pending (scan ${lim})`, () => viewsW.approveAllPending(0, lim));
    if (ok) refetch();
  };

  const totalAmount = items.reduce((sum, r) => sum + Number(formatUSDT(r.amount).replace(/,/g, '')), 0);
  const selectedAmount = items
    .filter((r) => selected.has(r.id))
    .reduce((sum, r) => sum + Number(formatUSDT(r.amount).replace(/,/g, '')), 0);

  return (
    <>
      {busy && <Loader text={busy} />}

      <div className="grid grid-3 mb-4">
        <div className="stat-card warning">
          <div className="stat-label">Total Pending</div>
          <div className="stat-value">{total}</div>
        </div>
        <div className="stat-card primary">
          <div className="stat-label">Page Volume</div>
          <div className="stat-value">${totalAmount.toLocaleString()}</div>
        </div>
        <div className="stat-card success">
          <div className="stat-label">Selected</div>
          <div className="stat-value">{selected.size}</div>
          <div className="stat-sub">${selectedAmount.toLocaleString()}</div>
        </div>
      </div>

      <Card title="Pending Withdrawal Requests" glow>
        {loading ? <InlineLoader /> : items.length === 0 ? (
          <div className="empty-state">No pending requests</div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-3 wrap gap-3">
              <div className="flex gap-2 wrap">
                <Button size="sm" variant="ghost" onClick={selectAll}>
                  {selected.size === items.length ? 'DESELECT ALL' : 'SELECT ALL'}
                </Button>
                <Button size="sm" variant="success" disabled={selected.size === 0} onClick={approveBatch}>
                  APPROVE SELECTED ({selected.size})
                </Button>
                <Button size="sm" variant="danger" disabled={selected.size === 0} onClick={rejectBatch}>
                  REJECT SELECTED
                </Button>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  className="input"
                  type="number"
                  value={scanLimit}
                  onChange={(e) => setScanLimit(e.target.value)}
                  style={{ maxWidth: 100 }}
                  title="Scan limit"
                />
                <Button size="sm" variant="primary" onClick={approveAll}>APPROVE ALL PENDING</Button>
              </div>
            </div>

            <div className="table-wrap table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Req #</th>
                    <th>User ID</th>
                    <th>Wallet</th>
                    <th className="text-right">Amount</th>
                    <th>Requested</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
                      </td>
                      <td className="text-primary">#{r.id}</td>
                      <td>#{r.userId}</td>
                      <td>{shortAddr(r.wallet)}</td>
                      <td className="text-right text-warning">${formatUSDT(r.amount)}</td>
                      <td>{formatDateShort(r.requestedAt)}</td>
                      <td className="text-center">
                        <div className="flex gap-2 justify-center">
                          <Button size="sm" variant="success" onClick={() => approveOne(r.id)}>APPROVE</Button>
                          <Button size="sm" variant="danger" onClick={() => rejectOne(r.id)}>REJECT</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={pg === 0} onClick={() => setPg((p) => Math.max(0, p - 1))}>← PREV</button>
                <span className="page-info">Page {pg + 1} / {totalPages}</span>
                <button className="page-btn" disabled={pg >= totalPages - 1} onClick={() => setPg((p) => p + 1)}>NEXT →</button>
              </div>
            )}
          </>
        )}
      </Card>
    </>
  );
}
